import React, { useState, useEffect, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, 
  ShoppingCart, 
  Plus,
  Minus,
  Filter,
  Pill,
  Heart,
  ChevronRight,
  Grid,
  List
} from 'lucide-react';

// Cart Context
const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  const addToCart = (medicine) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === medicine.id);
      if (existing) {
        return prev.map(item => 
          item.id === medicine.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...medicine, quantity: 1 }];
    });
  };

  const removeFromCart = (id) => {
    setCartItems(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id, quantity) => {
    if (quantity <= 0) {
      removeFromCart(id);
    } else {
      setCartItems(prev => prev.map(item =>
        item.id === id ? { ...item, quantity } : item
      ));
    }
  };

  const clearCart = () => setCartItems([]);

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, cartTotal, cartCount }}>
      {children}
    </CartContext.Provider>
  );
};

// Mock medicines data
const getMockMedicines = (lang) => [
  { id: 1, name: lang === 'ar' ? 'بانادول' : 'Panadol', category: 'pain', price: 5.99, image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop', description: lang === 'ar' ? 'مسكن للآلام وخافض للحرارة' : 'Pain reliever and fever reducer', prescription: false, stock: 50 },
  { id: 2, name: lang === 'ar' ? 'أموكسيسيلين' : 'Amoxicillin', category: 'antibiotics', price: 12.99, image: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=200&h=200&fit=crop', description: lang === 'ar' ? 'مضاد حيوي' : 'Antibiotic', prescription: true, stock: 30 },
  { id: 3, name: lang === 'ar' ? 'فيتامين سي' : 'Vitamin C', category: 'vitamins', price: 8.99, image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?w=200&h=200&fit=crop', description: lang === 'ar' ? 'مكمل غذائي' : 'Dietary supplement', prescription: false, stock: 100 },
  { id: 4, name: lang === 'ar' ? 'أوميبرازول' : 'Omeprazole', category: 'digestive', price: 15.99, image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=200&h=200&fit=crop', description: lang === 'ar' ? 'لعلاج الحموضة' : 'Acid reflux treatment', prescription: false, stock: 45 },
  { id: 5, name: lang === 'ar' ? 'إيبوبروفين' : 'Ibuprofen', category: 'pain', price: 7.99, image: 'https://images.unsplash.com/photo-1550572017-4e6d9438c8bc?w=200&h=200&fit=crop', description: lang === 'ar' ? 'مضاد للالتهاب' : 'Anti-inflammatory', prescription: false, stock: 75 },
  { id: 6, name: lang === 'ar' ? 'ميتفورمين' : 'Metformin', category: 'diabetes', price: 9.99, image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop', description: lang === 'ar' ? 'لعلاج السكري' : 'Diabetes treatment', prescription: true, stock: 40 },
  { id: 7, name: lang === 'ar' ? 'ليزينوبريل' : 'Lisinopril', category: 'heart', price: 11.99, image: 'https://images.unsplash.com/photo-1585435557343-3b092031a831?w=200&h=200&fit=crop', description: lang === 'ar' ? 'لعلاج ضغط الدم' : 'Blood pressure treatment', prescription: true, stock: 35 },
  { id: 8, name: lang === 'ar' ? 'سيتريزين' : 'Cetirizine', category: 'allergy', price: 6.99, image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?w=200&h=200&fit=crop', description: lang === 'ar' ? 'مضاد للحساسية' : 'Antihistamine', prescription: false, stock: 60 },
];

const categories = [
  { value: 'all', label: { en: 'All', ar: 'الكل' } },
  { value: 'pain', label: { en: 'Pain Relief', ar: 'مسكنات' } },
  { value: 'antibiotics', label: { en: 'Antibiotics', ar: 'مضادات حيوية' } },
  { value: 'vitamins', label: { en: 'Vitamins', ar: 'فيتامينات' } },
  { value: 'digestive', label: { en: 'Digestive', ar: 'الجهاز الهضمي' } },
  { value: 'diabetes', label: { en: 'Diabetes', ar: 'السكري' } },
  { value: 'heart', label: { en: 'Heart', ar: 'القلب' } },
  { value: 'allergy', label: { en: 'Allergy', ar: 'الحساسية' } },
];

const MedicinesPage = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language;

  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      setMedicines(getMockMedicines(lang));
      setLoading(false);
    }, 500);
  }, [lang]);

  const addToCart = (medicine) => {
    setCartItems(prev => {
      const existing = prev.find(item => item.id === medicine.id);
      if (existing) {
        return prev.map(item => 
          item.id === medicine.id 
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { ...medicine, quantity: 1 }];
    });
    toast.success(
      lang === 'ar' ? 'تمت الإضافة إلى السلة' : 'Added to cart',
      { description: medicine.name }
    );
  };

  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const filteredMedicines = medicines.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || m.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="medicines-page">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b dark:border-slate-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 max-w-xl">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={lang === 'ar' ? 'ابحث عن دواء...' : 'Search medicines...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Button 
              variant="outline" 
              className="relative"
              onClick={() => navigate('/pharmacy/cart', { state: { cartItems } })}
              data-testid="cart-button"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center bg-green-600">
                  {cartCount}
                </Badge>
              )}
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Categories */}
        <div className="flex gap-2 overflow-x-auto pb-4 mb-6 scrollbar-hide">
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className={selectedCategory === cat.value ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {lang === 'ar' ? cat.label.ar : cat.label.en}
            </Button>
          ))}
        </div>

        {/* Medicines Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <Skeleton className="h-32 w-full" />
                <CardContent className="p-3">
                  <Skeleton className="h-4 w-2/3 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-2" />
                  <Skeleton className="h-8 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredMedicines.length === 0 ? (
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardContent className="p-8 text-center">
              <Pill className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {lang === 'ar' ? 'لم يتم العثور على أدوية' : 'No medicines found'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredMedicines.map(medicine => (
              <Card key={medicine.id} className="dark:bg-slate-800 border-0 shadow-sm overflow-hidden" data-testid={`medicine-${medicine.id}`}>
                <div className="relative h-32 bg-slate-100">
                  <img src={medicine.image} alt={medicine.name} className="w-full h-full object-cover" />
                  {medicine.prescription && (
                    <Badge className="absolute top-2 left-2 bg-red-500 text-xs">
                      {lang === 'ar' ? 'بوصفة' : 'Rx'}
                    </Badge>
                  )}
                </div>
                <CardContent className="p-3">
                  <h3 className="font-medium text-slate-900 text-sm mb-1 truncate">{medicine.name}</h3>
                  <p className="text-xs text-slate-500 mb-2 line-clamp-1">{medicine.description}</p>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-green-600">${medicine.price}</span>
                    <Button 
                      size="sm" 
                      className="h-8 bg-green-600 hover:bg-green-700"
                      onClick={() => addToCart(medicine)}
                      disabled={medicine.prescription}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Floating Cart */}
        {cartCount > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
            <Button 
              className="bg-green-600 hover:bg-green-700 shadow-lg px-6"
              onClick={() => navigate('/pharmacy/cart', { state: { cartItems } })}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {lang === 'ar' ? 'عرض السلة' : 'View Cart'}
              <Badge className="ml-2 bg-white text-green-600">{cartCount}</Badge>
              <span className="mx-2">|</span>
              <span>${cartTotal.toFixed(2)}</span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MedicinesPage;
